import { getIconPath } from '@/common'
import { SmallIcon } from '@/components/Icon'
import ItemLibrary, { LibraryClickHandler } from '@/components/ItemLibrary'
import Data from '@/data'
import Trans from '@/data/trans'
import { CraftingBenchOption, ItemDefinition } from '@/data/types'
import { LibraryNode } from '@/library'
import { itemBaseIcon, itemCreate, itemCreateNormal, itemUpdateStats } from '@/logic/item'
import { modStatsMax } from '@/logic/mods'
import { WithItemTemplateTooltip, WithItemTooltip } from '@/tooltip'
import { AtlasEmbed, AtlasStrategy, Embed } from '@/types/embed'
import Dialog from '@/ui/dialog'
import Select, { underlinerFunc } from '@/ui/select'
import { searchRegex } from '@/utils'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React from 'react'
import { TreeDataContext } from './common'
import { PassiveSidebar } from './sidebar'

import { Item } from '@/types/item'
import { SvgIcon } from '@maxroll-media-group/common-libs'
import './AtlasStrategy.scss'

const defaultStrategy: AtlasStrategy = {
  sidebar: [],
  device: [],
  sextants: []
}

function ItemImage({ item }: { item: Item | undefined }) {
  if (!item) return null
  const base = item.unique ? Data.uniques[item.unique] : Data.items[item.base]
  const icon = itemBaseIcon(base, item.map_tier)
  if (!icon) return null
  return <div className="poe-icon" style={{ backgroundImage: `url(${getIconPath(icon)})` }} />
}

function MapSelector({
  fragments,
  onSelect,
  showFill
}: {
  fragments?: boolean
  onSelect: (id: string | undefined, fill?: boolean, tier?: number) => void
  showFill?: boolean
}) {
  const onClick = React.useCallback<LibraryClickHandler>(
    (e, id, type, fill, tier) => onSelect(id, fill, tier),
    [onSelect]
  )
  const categories = React.useMemo<LibraryNode>(
    () => ({
      name: 'Maps',
      filter: (item: ItemDefinition, unique?: boolean) =>
        item &&
        ((item.item_class === 'Map' && (unique || item.visual_identity?.dds_file?.match(/\/new\//))) ||
          (fragments && item.item_class === 'MapFragment'))
    }),
    [fragments]
  )

  return (
    <Dialog className="poe-ItemSelector" modal title="Select Item" onClose={() => onSelect(undefined)}>
      <div className="poe-ItemSelector-content">
        <ItemLibrary
          root={categories}
          onClick={onClick}
          uniques
          hideTree
          customBox={showFill ? 'Fill remaining slots' : undefined}
          selectMapTier
        />
      </div>
    </Dialog>
  )
}

function MapSlot({
  item,
  onChange,
  fragments,
  showFill
}: {
  item: Item | undefined
  onChange?: (id: string | undefined, fill?: boolean, tier?: number) => void
  fragments?: boolean
  showFill?: boolean
}) {
  const [selecting, setSelecting] = React.useState(false)
  const onSelect = React.useCallback(
    (item: string | undefined, fill?: boolean, tier?: number) => {
      setSelecting(false)
      if (item) onChange?.(item, fill, tier)
    },
    [onChange]
  )
  const onEmpty = React.useCallback(
    (e: React.UIEvent) => {
      e.preventDefault()
      onChange?.(undefined)
    },
    [onChange]
  )
  return (
    <>
      <WithItemTooltip item={item!} disabled={!item}>
        <div
          className={classNames('poe-slot', { 'poe-empty': !item })}
          onClick={() => onChange && setSelecting(true)}
          onContextMenu={onEmpty}>
          <ItemImage item={item} />
        </div>
      </WithItemTooltip>
      {!!selecting && !!onChange && <MapSelector fragments={fragments} onSelect={onSelect} showFill={showFill} />}
    </>
  )
}

function benchOptionName(opt: CraftingBenchOption) {
  if (opt.name) return opt.name
  const mod = Data.mods[opt.actions.add_explicit_mod!]
  if (!mod) return 'Unknown mod'
  const stats = modStatsMax(mod)
  const lines = Trans.atlas.formatStats(stats)
  return lines.join('\n')
}

type BenchOption = {
  id: string
  name: string
  mod_type?: string
}

function renderBenchOption({ id, name }: BenchOption) {
  const opt = Data.crafting_bench_options.find((opt) => opt.id === id)
  if (!opt) return null
  return (
    <div className="poe-bench-option">
      <div className="poe-name">{name}</div>
      <div className="poe-cost">
        {Object.entries(opt.cost).map(([id, value]) => (
          <span key={id}>
            Cost: {value}x{' '}
            <WithItemTemplateTooltip name={id}>
              <SmallIcon icon={itemBaseIcon(Data.items[id])} />
            </WithItemTemplateTooltip>
          </span>
        ))}
      </div>
    </div>
  )
}

const renderArrow = (disabled: boolean, className: string) => (
  <SvgIcon icon={disabled ? 'lock' : 'chevron-down'} className={classNames('ui-Select-arrow', className)} />
)

function CraftingOption({
  value,
  onChange,
  hasSeventhGate
}: {
  value: string | undefined
  onChange?: (id: string | undefined) => void
  hasSeventhGate?: boolean
}) {
  const options = React.useMemo(() => {
    const list: CraftingBenchOption[] = []
    for (const opt of Data.crafting_bench_options) {
      if (!opt.item_classes.includes('Map')) continue
      if (!hasSeventhGate && opt.level > 100) continue
      const pos = list.findIndex((o) => o.mod_type === opt.mod_type)
      if (pos >= 0) list[pos] = opt
      else list.push(opt)
    }
    return list.map<BenchOption>((opt) => ({
      id: opt.id,
      name: benchOptionName(opt),
      mod_type: opt.mod_type
    }))
  }, [hasSeventhGate])
  const currentValue =
    options.find((opt) => opt.id === value) ||
    options.find((opt) => opt.mod_type === 'MapItemDropQuantity') ||
    options[0]
  const wrapperNode = document.querySelector('.poeplanner-wrapper')
  if (!onChange) return renderBenchOption(currentValue)
  return (
    <Select
      options={options}
      value={currentValue}
      className="poe-BenchSelect"
      valueClass="ui-Select-value"
      menuClass="ui-Select-menu"
      optionClass="ui-Select-option"
      renderOption={renderBenchOption}
      renderValue={renderBenchOption}
      renderArrow={renderArrow}
      portalTo={wrapperNode}
      onChange={(opt) => onChange(opt?.id)}
    />
  )
}

function SextantPicker({ onSelect, onClose }: { onSelect: (id: string) => void; onClose: () => void }) {
  const [search, setSearch] = React.useState('')
  const onSetSearch = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), [])
  const onClearSearch = React.useCallback(() => setSearch(''), [])

  const options = React.useMemo(() => {
    const list = []
    for (const [id, mod] of Object.entries(Data.mods)) {
      if (mod.domain !== 'atlas') continue
      const stats = modStatsMax(mod)
      const lines = Trans.atlas.formatStats(stats)
      list.push({ id, lines })
    }
    return list
  }, [])
  const searchRe = React.useMemo(() => searchRegex(search, true), [search])

  return (
    <Dialog modal className="tip-SextantPicker" title="Choose Sextant Modifier" onClose={onClose}>
      <Dialog.Content>
        <div className="tip-search">
          <input placeholder="Search..." spellCheck={false} value={search} onChange={onSetSearch} />
          {!!search.trim() && <FontAwesomeIcon icon={faTimesCircle} className="tip-clear" onClick={onClearSearch} />}
        </div>
        <div className="tip-options">
          {options.map(({ id, lines }) => {
            if (searchRe && !lines.some((line) => line.match(searchRe))) return null
            return (
              <div
                key={id}
                className="tip-option"
                onClick={() => {
                  onSelect(id)
                  onClose()
                }}>
                {lines.map((line, index) => (
                  <div key={index}>{underlinerFunc(line, searchRe)}</div>
                ))}
              </div>
            )
          })}
        </div>
      </Dialog.Content>
    </Dialog>
  )
}

function AtlasSextant({
  value,
  onChange
}: {
  value: string | undefined
  onChange?: (value: string | undefined) => void
}) {
  const [selecting, setSelecting] = React.useState(false)
  const icon = Data.items['Metadata/Items/Currency/CurrencyItemisedSextantModifier'].visual_identity.dds_file
  const item = React.useMemo(() => {
    const item = itemCreateNormal('Metadata/Items/Currency/CurrencyItemisedSextantModifier')
    item.rarity = 'normal'
    if (value && Data.mods[value]) {
      item.mods = {
        enchant: { [value]: modStatsMax(Data.mods[value]) },
        implicit: {}
      }
    }
    return itemUpdateStats(item)
  }, [value])
  const onContextMenu = React.useCallback(
    (e: React.UIEvent) => {
      e.preventDefault()
      if (onChange) onChange(undefined)
    },
    [onChange]
  )
  if (!onChange && !value) return null
  return (
    <>
      <WithItemTooltip item={item} disabled={!value || !Data.mods[value]} options={{ domain: 'atlas' }}>
        <div
          className={classNames('poe-sextant', { 'poe-empty': !value })}
          style={{ backgroundImage: `url(${getIconPath(icon)})` }}
          onClick={() => onChange && setSelecting(true)}
          onContextMenu={onContextMenu}
        />
      </WithItemTooltip>
      {!!(selecting && onChange) && <SextantPicker onSelect={onChange} onClose={() => setSelecting(false)} />}
    </>
  )
}

function AtlasAltar({ value, onChange }: { value?: number; onChange?: (value: number) => void }) {
  return (
    <div className="poe-altar">
      {!!(onChange || value === 1) && (
        <div className={classNames('poe-altar-1', { 'poe-active': value === 1 })} onClick={() => onChange?.(1)} />
      )}
      {!!(onChange || value === 3) && (
        <div className={classNames('poe-altar-3', { 'poe-active': value === 3 })} onClick={() => onChange?.(3)} />
      )}
      {!!(onChange || value === 2) && (
        <div className={classNames('poe-altar-2', { 'poe-active': value === 2 })} onClick={() => onChange?.(2)} />
      )}
    </div>
  )
}

export function AtlasStrategyView({
  embed,
  onChange
}: {
  embed: AtlasEmbed
  onChange?: (value: Embed | ((prev: Embed) => Embed)) => void
}) {
  const data = React.useContext(TreeDataContext)
  const hasSeventhGate = React.useMemo(() => data.hasSeventhGate(), [data])
  const strategy = { ...defaultStrategy, ...embed.strategy }
  const onEquipSidebar =
    onChange &&
    ((index: number, value: string | undefined, fill?: boolean, tier?: number) =>
      onChange((embed) => {
        if (embed.type !== 'atlas') return embed
        embed = { ...embed }
        embed.strategy = { ...defaultStrategy, ...embed.strategy }
        embed.strategy.sidebar = embed.strategy.sidebar.slice()
        const item = value ? itemCreate(value) : undefined
        if (item) item.rarity = 'normal'
        if (item && tier) item.map_tier = tier
        embed.strategy.sidebar[index] = item
        if (fill) {
          for (let i = 0; i < 12; ++i) {
            if (!embed.strategy.sidebar[i]) embed.strategy.sidebar[i] = item
          }
        }
        return embed
      }))
  const onEquipDevice =
    onChange &&
    ((index: number, value: string | undefined, fill?: boolean, tier?: number) =>
      onChange((embed) => {
        if (embed.type !== 'atlas') return embed
        embed = { ...embed }
        embed.strategy = { ...defaultStrategy, ...embed.strategy }
        embed.strategy.device = embed.strategy.device.slice()
        const item = value ? itemCreate(value) : undefined
        if (item && tier) item.map_tier = tier
        embed.strategy.device[index] = item
        return embed
      }))
  const onChangeBench =
    onChange &&
    ((value: string | undefined) =>
      onChange((embed) => {
        if (embed.type !== 'atlas') return embed
        embed = { ...embed }
        embed.strategy = { ...defaultStrategy, ...embed.strategy }
        embed.strategy.bench = value
        return embed
      }))
  const onChangeSextant =
    onChange &&
    ((index: number, value: string | undefined) =>
      onChange((embed) => {
        if (embed.type !== 'atlas') return embed
        embed = { ...embed }
        embed.strategy = { ...defaultStrategy, ...embed.strategy }
        embed.strategy.sextants = embed.strategy.sextants.slice()
        embed.strategy.sextants[index] = value
        return embed
      }))
  const onChangeAltar =
    onChange &&
    ((value: number) =>
      onChange((embed) => {
        if (embed.type !== 'atlas') return embed
        embed = { ...embed }
        embed.strategy = { ...defaultStrategy, ...embed.strategy }
        embed.strategy.altar = embed.strategy.altar === value ? undefined : value
        return embed
      }))
  return (
    <div className="poe-AtlasStrategy">
      <div className="poe-map-bar">
        {[...Array(12)].map((_, index) => (
          <MapSlot
            key={index}
            item={strategy.sidebar[index]}
            onChange={onEquipSidebar && ((value, fill, tier) => onEquipSidebar(index, value, fill, tier))}
            showFill
          />
        ))}
      </div>
      <div className="poe-main">
        <div className="poe-map-device">
          {[...Array(6)].map((_, index) => (
            <MapSlot
              key={index}
              item={strategy.device[index]}
              onChange={onEquipDevice && ((value, fill, tier) => onEquipDevice(index, value, fill, tier))}
              fragments
            />
          ))}
        </div>
        <CraftingOption value={strategy.bench} onChange={onChangeBench} hasSeventhGate={hasSeventhGate} />
        {(embed?.version || 0) < 324 && (
          <div className="poe-sextants">
            {[...Array(4)].map((_, index) => (
              <AtlasSextant
                key={index}
                value={strategy.sextants[index]}
                onChange={onChangeSextant && ((value) => onChangeSextant(index, value))}
              />
            ))}
          </div>
        )}
        <AtlasAltar value={strategy.altar} onChange={onChangeAltar} />
      </div>
    </div>
  )
}

export function AtlasStrategyBar({
  embed,
  onChange
}: {
  embed: AtlasEmbed
  onChange?: (value: Embed | ((prev: Embed) => Embed)) => void
}) {
  return (
    <PassiveSidebar className="poe-strategy" title="Strategy">
      <AtlasStrategyView embed={embed} onChange={onChange} />
    </PassiveSidebar>
  )
}
